import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { getProvinces } from '@/services/clients.js';
import { useDropdownPosition } from '@/components/geo/useDropdownPosition.js';

export default function ProvinciaSelect({
  label,
  value,
  onChange,
  onOptionSelect,
  onBlur,
  placeholder = 'Seleccionar provincia',
  disabled = false,
  required = false,
  error = '',
}) {
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const { dropdownRef, panelStyle, placement } = useDropdownPosition(wrapperRef, open);

  useEffect(() => {
    getProvinces()
      .then(setOptions)
      .catch(error => console.error('Error cargando provincias', error));
  }, []);

  useEffect(() => {
    if (!value) {
      setQuery('');
      return;
    }
    const selected = options.find(option => String(option.id) === String(value));
    if (selected) {
      setQuery(selected.nombre);
    }
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = event => {
      const clickedOutsideWrapper = wrapperRef.current ? !wrapperRef.current.contains(event.target) : true;
      const clickedOutsideDropdown = dropdownRef.current ? !dropdownRef.current.contains(event.target) : true;
      if (clickedOutsideWrapper && clickedOutsideDropdown) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef]);

  const filtered = useMemo(() => {
    if (!query) return options;
    return options.filter(option =>
      option.nombre.toLowerCase().includes(query.toLowerCase())
    );
  }, [options, query]);

  const selectedOption = useMemo(
    () => options.find(option => String(option.id) === String(value)),
    [options, value]
  );

  const handleSelect = option => {
    if (onChange) onChange(option.id);
    if (onOptionSelect) onOptionSelect(option);
    setQuery(option.nombre);
    setOpen(false);
  };

  const handleInputBlur = () => {
    setOpen(false);
    setQuery(selectedOption?.nombre ?? '');
    if (onBlur) onBlur();
  };

  return (
    <div ref={wrapperRef} className="relative">
      {label ? (
        <label className="mb-1 block text-sm font-medium text-[rgb(var(--fg))]">
          {label}
          {required ? <span className="ml-1 text-[rgb(var(--danger,255_99_99))]">*</span> : null}
        </label>
      ) : null}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--muted))]" />
        <input
          type="text"
          value={query}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          aria-autocomplete="list"
          onChange={event => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full rounded-xl border ${error ? 'border-[rgb(var(--danger,255_99_99))]' : 'border-[rgb(var(--border))]'} bg-[rgba(var(--bg))] px-9 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]`}
        />
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--muted))]" />
      </div>
      {error ? (
        <p className="mt-1 text-xs text-[rgb(var(--danger,255_99_99))]">{error}</p>
      ) : null}
      {open && panelStyle && typeof document !== 'undefined'
        ? createPortal(
          <div
            ref={dropdownRef}
            style={panelStyle}
            className="overflow-hidden rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.98)] shadow-[0_20px_40px_rgba(var(--shadow),0.2)]"
            data-placement={placement}
          >
            <ul className="overflow-y-auto py-1 text-sm" style={{ maxHeight: panelStyle.maxHeight }}>
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-[rgb(var(--muted))]">Sin resultados.</li>
            ) : null}
            {filtered.map(option => (
              <li key={option.id}>
                <button
                  type="button"
                  onMouseDown={event => {
                    event.preventDefault();
                    handleSelect(option);
                  }}
                  className="w-full px-4 py-2 text-left text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  {option.nombre}
                </button>
              </li>
            ))}
          </ul>
          </div>,
          document.body
        )
        : null}
    </div>
  );
}
